import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Sparkle, Microphone, FileText, ChartLine, SpinnerGap } from "phosphor-react";
import { authFetchJson } from "../auth/authApi";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

/**
 * SmartToolsUsageMeter - Compact usage summary for Smart Tools
 * Shows AI calls, voice notes, and job summaries used this month.
 */
export default function SmartToolsUsageMeter({ hasSmartTools = false }) {
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchUsage();
  }, []);

  const fetchUsage = async () => {
    try {
      const data = await authFetchJson(`${API}/ai/usage/summary?period=month`);
      setUsage(data);
      setError(null);
    } catch (err) {
      console.error("Failed to fetch usage:", err);
      setError("Unable to load usage data");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Card data-testid="smart-tools-usage-meter" className="border border-zinc-200 bg-zinc-50/50">
        <CardContent className="flex items-center justify-center py-8">
          <SpinnerGap className="w-5 h-5 animate-spin text-zinc-400" />
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card data-testid="smart-tools-usage-meter" className="border border-zinc-200 bg-zinc-50/50">
        <CardContent className="py-6">
          <p className="text-sm text-zinc-500 text-center">{error}</p>
        </CardContent>
      </Card>
    );
  }

  // Format period display
  const formatPeriod = () => {
    if (!usage?.period_start) return "This Month";
    const start = new Date(usage.period_start);
    return start.toLocaleDateString("en-US", { month: "long", year: "numeric" });
  };

  return (
    <Card data-testid="smart-tools-usage-meter" className="border border-zinc-200 bg-gradient-to-br from-white to-zinc-50">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base font-semibold flex items-center gap-2">
            <ChartLine className="w-4 h-4 text-orange-500" />
            Smart Tools Usage
          </CardTitle>
          <Badge variant="outline" className="text-xs font-normal text-zinc-500">
            {formatPeriod()}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* AI Calls */}
        <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-zinc-100">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-50 rounded-md">
              <Sparkle className="w-4 h-4 text-purple-500" />
            </div>
            <div>
              <p className="text-sm font-medium text-zinc-700">AI Calls</p>
              <p className="text-xs text-zinc-400">Total AI operations</p>
            </div>
          </div>
          <span className="text-xl font-semibold text-zinc-900" data-testid="usage-ai-calls">
            {usage?.ai_calls ?? 0}
          </span>
        </div>

        {/* Voice Notes */}
        <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-zinc-100">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-50 rounded-md">
              <Microphone className="w-4 h-4 text-blue-500" />
            </div>
            <div>
              <p className="text-sm font-medium text-zinc-700">Voice Notes</p>
              <p className="text-xs text-zinc-400">Recorded & transcribed</p>
            </div>
          </div>
          <span className="text-xl font-semibold text-zinc-900" data-testid="usage-voice-notes">
            {usage?.voice_notes_count ?? 0}
          </span>
        </div>

        {/* Job Summaries */}
        <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-zinc-100">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-50 rounded-md">
              <FileText className="w-4 h-4 text-green-500" />
            </div>
            <div>
              <p className="text-sm font-medium text-zinc-700">Job Summaries</p>
              <p className="text-xs text-zinc-400">AI-generated summaries</p>
            </div>
          </div>
          <span className="text-xl font-semibold text-zinc-900" data-testid="usage-job-summaries">
            {usage?.job_summary_ai_count ?? 0}
          </span>
        </div>

        {/* Upgrade prompt for non-subscribers */}
        {!hasSmartTools && (
          <div className="mt-4 p-3 bg-orange-50 rounded-lg border border-orange-100">
            <p className="text-sm text-orange-700 text-center">
              <span className="font-medium">Free tier.</span> Upgrade to Smart Tools for unlimited AI features.
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
